import {
  Component,
  ElementRef,
  HostBinding,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  viewChild,
  ChangeDetectorRef,
  input,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import UserUtil from 'app/core/utilities/user-util';
import { AppState, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks } from 'app/utils/media/stream';
import RingColor from 'entity/RingColor';
import User from 'entity/User';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import { DeviceService } from '../../services/device/device.service';
import { EmoteService, UserEmote } from '../../services/emote/emote.service';
import { SceneService } from '../../services/scene.service';
import { GroupShareScreenParticipantService } from '../../services/share-screen-group/group-share-screen-participant.service';
import { VideoRoomService } from '../../services/video-room.service';
import { Player } from '../../social-pixi-canvas/player';
import { PopOutState, socialAppActions, socialAppSelectors } from '../../state/app';
import { BrowserService } from '../../services/browser.service';
const UNDEFINED_STREAM = { id: uuidv4(), stream: undefined };

@Component({
  selector: 'app-presentation-group-share',
  templateUrl: './presentation-group-share.component.html',
  styleUrls: ['./presentation-group-share.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationGroupShareComponent extends Loggable implements OnInit, OnDestroy {
  private groupShareScreenParticipantService = inject(GroupShareScreenParticipantService);
  private store = inject<Store<AppState>>(Store);
  private deviceService = inject(DeviceService);
  private videoRoomService = inject(VideoRoomService);
  private sceneService = inject(SceneService);
  private emoteService = inject(EmoteService);
  private browserService = inject(BrowserService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  // Using input() with a signal-based model for @HostBinding
  public readonly isGalleryDrawerOpen = input<boolean>(false);
  @HostBinding('class.drawer-open')
  protected get galleryDrawerOpenClass(): boolean {
    return this.isGalleryDrawerOpen();
  }

  // Using input() with a signal-based model for @HostBinding
  public readonly isCaptionsOpen = input<boolean>(false);
  @HostBinding('class.captions-open')
  protected get captionsOpenClass(): boolean {
    return this.isCaptionsOpen();
  }

  // Using input() with a signal-based model for @HostBinding
  public readonly isGroupSharing = input<boolean>(false);
  @HostBinding('class.screenshare')
  protected get groupSharingClass(): boolean {
    return this.isGroupSharing();
  }
  private readonly _isPresentersDrawerOpen = signal<boolean>(true);
  @HostBinding('class.presenters-drawer-is-open')
  protected get isPresentersDrawerOpen(): boolean {
    return this._isPresentersDrawerOpen();
  }
  protected set isPresentersDrawerOpen(value: boolean) {
    this._isPresentersDrawerOpen.set(value);
  }

  public readonly user = signal<User | undefined>(undefined);
  public readonly screenStream = signal<MediaStream | undefined>(undefined);
  private readonly clickedOnBadge = signal<boolean>(false);
  public readonly userIsInteracting = signal<boolean>(false);
  protected readonly isVideoPlaying = signal<boolean>(false);
  protected readonly isUserSharingScreen = signal<boolean>(false);

  private readonly _viewSize = signal<StageSizeType>(StageSizeType.MEDIUM);

  public get viewSize(): StageSizeType {
    return this._viewSize();
  }

  public set viewSize(value: StageSizeType) {
    this._viewSize.set(value);
    this.groupShareScreenParticipantService.viewSize.set(value);
  }
  private movementListener?: (this: Window, ev: MouseEvent) => unknown;
  private movementListenerBlocked = false;
  private userIsInteractingTimeout?: Timeout;

  public readonly videoMuted = signal<boolean>(false);
  public readonly audioMuted = signal<boolean>(false);
  public readonly presenterEmote = signal<UserEmote | undefined>(undefined);
  public readonly presentersPlayer = signal<Player | undefined>(undefined);

  protected readonly isPopout = signal<PopOutState>({ mode: 'PRESENTER', open: false });
  protected readonly popOutVideo = signal<boolean>(false);
  protected readonly fullScreenVideo = signal<boolean>(true);

  protected readonly ringColors = signal<RingColor[]>([]);

  protected readonly isFullScreenMobile = signal<boolean>(false);
  protected readonly isMobile = signal<boolean>(false);

  public readonly screenShareZoom = signal<boolean>(true);
  protected readonly zoomScale = signal(PresentationGroupShareComponent.DEFAULT_ZOOM);
  protected readonly isDragging = signal<boolean>(false);
  protected readonly startX = signal<number>(1);
  protected readonly startY = signal<number>(1);
  protected readonly dragX = signal<number>(1);
  protected readonly dragY = signal<number>(1);
  protected readonly screenOffsetX = signal<number>(1);
  protected readonly screenOffsetY = signal<number>(1);

  private static readonly DEFAULT_ZOOM = 1;
  private static readonly ZOOM_MOD = 0.5;

  private destroyed$ = new ReplaySubject<boolean>(1);

  private readonly videoElement = viewChild<ElementRef<HTMLVideoElement>>('videoElement');
  private readonly videoWrapper = viewChild<ElementRef<HTMLDivElement>>('videoWrapper');
  private readonly presenter = viewChild<ElementRef<HTMLVideoElement>>('presenter');

  protected readonly groupShareId = signal<string>('');

  // Computed properties for presenter media states
  protected readonly isPresenterVideoMuted = computed(() => this.videoMuted());
  protected readonly isPresenterAudioMuted = computed(() => this.audioMuted());
  protected readonly hasPresenterEmote = computed(() => !!this.presenterEmote());
  protected readonly isCurrentUserPresenter = computed(() => this.groupShareId() === this.user()?.userId);

  protected readonly groupShareState = computed(() => {
    return this.groupShareScreenParticipantService.groupShareState();
  });

  protected readonly groupShareEnhancedState = computed(() => {
    return this.groupShareScreenParticipantService.groupShareEnhancedState();
  });

  // UI visibility states
  protected readonly showPresenterControls = computed(() => {
    return this.userIsInteracting() && !this.isFullScreenMobile() && this.isGroupSharing();
  });

  protected readonly showPresenterAvatar = computed(() => {
    return this.isPresentersDrawerOpen && this.isPresenterVideoMuted();
  });

  protected readonly hasActiveGroupShare = computed(() => {
    return !!this.screenStream() && this.isGroupSharing();
  });

  public constructor() {
    super();
    this.setDisplayName('GroupShareScreenParticipantComponent');

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => this.user.set(user));

    this.store
      .select(socialAppSelectors.isPopOut)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPopOut) => this.isPopout.set(isPopOut));

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.colors) {
          this.ringColors.set(visualIndicatorInfo.colors);
        }
      });
  }

  public handleMediaStreamChange({ id, stream }: { stream?: MediaStream; id: string }, isShare = true): void {
    const target = isShare ? this.videoElement() : this.presenter();
    assertDefined(target);
    const { nativeElement: videoElement } = target;

    if (videoElement.getAttribute('data-stream-id') === id) {
      this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }
    videoElement.srcObject = stream ?? null;
    videoElement.setAttribute('data-stream-id', id);

    if (!videoElement.oncanplay) {
      videoElement.oncanplay = () => {
        videoElement.oncanplay = null;
        videoElement
          .play()
          .then(() => {
            this.isVideoPlaying.set(true);
          })
          .catch((e) => {
            this.isVideoPlaying.set(false);
            this.error('handleMediaStreamChange: Failed to play video', e);
          });
      };
    }
  }

  public readonly stageSizeType = StageSizeType;

  public ngOnInit(): void {
    // Listen for share media streams using effect or subscribe to Signal value
    this.groupShareScreenParticipantService.mediaStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ id, stream }) => {
        this.handleMediaStreamChange({ id, stream });
        this.changeDetectorRef.markForCheck();
      });

    const presenterStream = signal<{ id: string; stream?: MediaStream }>(UNDEFINED_STREAM);

    // Listen for group share ID changes
    this.groupShareScreenParticipantService.groupShareId$
      .pipe(
        switchMap((groupShareId) => {
          this.groupShareId.set(groupShareId);
          this.getPresentersPlayer(groupShareId);
          this.handlePresenterMuted(groupShareId);
          this.handlePresentersEmote(groupShareId);
          this.changeDetectorRef.markForCheck();
          // if user is presenter, use camera stream
          if (groupShareId === this.user()?.userId) {
            this.isUserSharingScreen.set(true);
            return this.deviceService.hdCameraStream$.pipe(
              map((stream) => (stream ? { stream, id: stream.id } : UNDEFINED_STREAM)),
            );
            // else use video room stream
          } else {
            this.isUserSharingScreen.set(false);
            return this.videoRoomService.participantVideoStream$.pipe(
              map((streams) => streams[groupShareId] ?? UNDEFINED_STREAM),
            );
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => {
        presenterStream.set({ id, stream });
        this.handleMediaStreamChange({ id, stream }, false);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pop_out_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((popOutVideo) => this.popOutVideo.set(popOutVideo));
    this.declareListenerMethod();
    this.initListeners();
    this.isMobile.set(this.browserService.isMobile());
  }

  private declareListenerMethod(): void {
    this.movementListener = () => {
      // Blocked while animation
      if (this.movementListenerBlocked) {
        return;
      }

      this.userIsInteracting.set(true);
      if (this.userIsInteractingTimeout) {
        clearTimeout(this.userIsInteractingTimeout);
      }
      this.userIsInteractingTimeout = setTimeout(() => {
        this.userIsInteracting.set(false);

        // Block while animation
        this.movementListenerBlocked = true;
        setTimeout(() => (this.movementListenerBlocked = false), 500);
      }, 5000);
    };
  }

  private initListeners(): void {
    if (window && this.movementListener) {
      window.addEventListener('click', this.movementListener, false);
      window.addEventListener('mousemove', this.movementListener, false);
    }
    document.addEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
  }

  public readonly isFullScreen = computed(() => this._viewSize() === StageSizeType.FULL_SCREEN);

  // Create a HostBinding getter that unwraps the signal value
  @HostBinding('class.is-fullscreen')
  public get isFullScreenClass(): boolean {
    return this.isFullScreen();
  }

  public toggleFullScreen(force?: StageSizeType): void {
    const newSize = force ? force : this.isFullScreen() ? StageSizeType.MEDIUM : StageSizeType.FULL_SCREEN;
    this._viewSize.set(newSize);
    this.groupShareScreenParticipantService.viewSize.set(newSize);
    this.store.dispatch(socialAppActions.SET_PRESENTATION_STAGE_SIZE({ stageSize: newSize }));
  }

  public toggleVideoDrawer($event: Event): void {
    $event.stopPropagation();
    // don't double toggle when clicking on the button inside the drawer
    if (this.clickedOnBadge()) {
      this.clickedOnBadge.set(false);
      return;
    }
    this.isPresentersDrawerOpen = !this.isPresentersDrawerOpen;
  }

  protected getPresentersPlayer(groupShareId: string) {
    this.presentersPlayer.set(this.sceneService.sceneManager?.getParticipant(groupShareId));
  }

  // Add a computed signal for the presenter player based on groupShareId
  protected readonly computedPresentersPlayer = computed(() => {
    const groupId = this.groupShareId();
    return groupId ? this.sceneService.sceneManager?.getParticipant(groupId) : undefined;
  });

  private handlePresenterMuted(groupShareId: string) {
    // Store participant media state in a signal
    const participantMediaState = signal<Record<string, { videoMuted: boolean; audioMuted: boolean }>>({});

    this.store
      .select(socialAppSelectors.getParticipantMediaState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((participant) => {
        participantMediaState.set(participant);
        if (!participant[groupShareId]) {
          return;
        }
        this.videoMuted.set(participant[groupShareId].videoMuted);
        this.audioMuted.set(participant[groupShareId].audioMuted);
      });

    // We could create computed properties for this instead of manually updating the signals
    // Example:
    // const videoMuted = computed(() => {
    //   const state = participantMediaState();
    //   const id = this.groupShareId();
    //   return state[id]?.videoMuted ?? false;
    // });
  }

  private handlePresentersEmote(groupShareId: string) {
    // Store all emotes in a signal
    const allEmotes = signal<UserEmote[]>([]);

    this.emoteService.emoteStream$.pipe(takeUntil(this.destroyed$)).subscribe((emote) => {
      // Update the emotes signal
      const currentEmotes = allEmotes();
      allEmotes.set([...currentEmotes, emote]);

      // Update the presenter emote as before
      if (emote.userId === groupShareId && emote.timestamp > Date.now() - this.emoteService.emooteLength) {
        void this.updateEmote(emote);
      }
    });

    // We could create a computed for the current presenter's emote and use it in the template
    // Example:
    // this.presenterCurrentEmote = computed(() => {
    //   const id = this.groupShareId();
    //   const emotes = allEmotes();
    //   const now = Date.now();
    //
    //   return emotes.find(
    //     emote => emote.userId === id && emote.timestamp > now - this.emoteService.emooteLength
    //   );
    // });
  }

  private async updateEmote(emote: UserEmote) {
    this.presenterEmote.set(emote);
    await delayFor(this.emoteService.emooteLength);
    this.presenterEmote.set(undefined);
  }

  protected formatPresentersName(user?: User): string {
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
    return user ? UserUtil.trimName(20, name) : '';
  }

  // Add a computed signal for the presenter's name based on the current group share ID
  protected readonly presenterName = computed(() => {
    const groupId = this.groupShareId();
    if (!groupId) {
      return '';
    }

    // Create a formatter that returns the presenter's name based on the current user
    // We can enhance this later as needed
    return this.formatPresentersName(this.computedPresenterUser());
  });

  // Add a computed for the presenter user
  protected readonly computedPresenterUser = computed(() => {
    const groupId = this.groupShareId();
    if (!groupId) {
      return undefined;
    }

    // For simplicity, just use the current user if they are the presenter
    if (this.user()?.userId === groupId) {
      return this.user();
    }

    // Otherwise we'd get the user from participants but need more context
    // This can be enhanced later
    return undefined;
  });

  protected openBadge(userId?: string): void {
    this.clickedOnBadge.set(true);
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: userId ? userId : '' }));
  }

  public readonly isPresenterPopout = computed((): boolean => {
    return this.isPopout().mode === 'PRESENTER' && this.isPopout().open;
  });

  public readonly isScreenPopout = computed((): boolean => {
    return this.isPopout().mode === 'SCREENSHARE' && this.isPopout().open;
  });

  public readonly roomHasRingColors = computed((): boolean => {
    return !!this.ringColors().length;
  });

  public handleFullscreenChange() {
    const videoWrapperElement = this.videoWrapper()?.nativeElement;
    assertDefined(videoWrapperElement);

    if (this.isFullScreenMobile()) {
      if (document.fullscreenElement === videoWrapperElement) {
        document.exitFullscreen().catch((e) => {
          this.error('handleFullScreen: Failed to toggle OFF fullscreen on video', e);
        });

        this.isFullScreenMobile.set(false);
      }
    } else {
      videoWrapperElement.requestFullscreen().catch((e) => {
        this.error('handleFullScreen: Failed to toggle ON fullscreen on video', e);
      });
      this.isFullScreenMobile.set(true);
    }
  }

  private onFullScreenChange() {
    if (!document.fullscreenElement) {
      //Event to turn off fullscreen flag if fullscreen disabled by external events
      this.isFullScreenMobile.set(false);
    }
  }

  public readonly isFullScreenEnabled = computed(() => {
    return !!document.fullscreenEnabled && this.isMobile() && !!this.videoElement()?.nativeElement;
  });

  protected startDrag(event: MouseEvent) {
    if (!this.isZoomedIn() || !this.screenShareZoom()) {
      return;
    }
    this.isDragging.set(true);
    this.startX.set(event.clientX - this.screenOffsetX());
    this.startY.set(event.clientY - this.screenOffsetY());
  }

  protected endDrag(event: MouseEvent) {
    if (!this.screenShareZoom()) {
      return;
    }
    this.isDragging.set(false);
    this.startX.set(event.clientX);
    this.startY.set(event.clientY);
  }

  protected dragScreenShare(event: MouseEvent) {
    if (this.isZoomedIn() && this.isDragging()) {
      this.dragX.set(event.clientX - this.startX());
      this.dragY.set(event.clientY - this.startY());
      this.adjustToBounds();
    }
  }

  protected leaveDraggableArea() {
    if (this.isDragging()) {
      this.isDragging.set(false);
    }
  }

  protected controlZoom(action: 'in' | 'out' | 'reset') {
    if (action === 'in') {
      this.zoomIn();
    } else if (action === 'out') {
      this.zoomOut();
    } else if (action === 'reset') {
      this.resetToDefaultZoom();
    }
  }

  public readonly isZoomedIn = computed(() => {
    return this.zoomScale() > PresentationGroupShareComponent.DEFAULT_ZOOM;
  });

  private zoomIn() {
    this.zoomScale.set(this.zoomScale() + PresentationGroupShareComponent.ZOOM_MOD);
  }

  private zoomOut() {
    if (!this.isZoomedIn()) {
      return;
    }
    this.zoomScale.set(this.zoomScale() - PresentationGroupShareComponent.ZOOM_MOD);
    // reset position if it is the last zoom out
    if (!this.isZoomedIn()) {
      this.screenOffsetX.set(1);
      this.screenOffsetY.set(1);
      return;
    }
    this.screenOffsetX.set(this.screenOffsetX() - this.screenOffsetX() * PresentationGroupShareComponent.ZOOM_MOD);
    this.screenOffsetY.set(this.screenOffsetY() - this.screenOffsetY() * PresentationGroupShareComponent.ZOOM_MOD);
  }

  private resetToDefaultZoom() {
    this.zoomScale.set(PresentationGroupShareComponent.DEFAULT_ZOOM);
    this.startX.set(1);
    this.startY.set(1);
    this.dragX.set(1);
    this.dragY.set(1);
    this.screenOffsetX.set(1);
    this.screenOffsetY.set(1);
  }

  private adjustToBounds() {
    const containerRect = this.videoWrapper()?.nativeElement.getBoundingClientRect();
    const contentRect = this.videoElement()?.nativeElement.getBoundingClientRect();

    if (!containerRect || !contentRect) {
      return;
    }

    const maxOffsetX = (contentRect.width - containerRect.width) / 2;
    const maxOffsetY = (contentRect.height - containerRect.height) / 2;

    this.screenOffsetX.set(Math.max(-maxOffsetX, Math.min(maxOffsetX, this.dragX())));
    this.screenOffsetY.set(Math.max(-maxOffsetY, Math.min(maxOffsetY, this.dragY())));
  }

  public readonly videoStyles = computed(() => {
    const cursor = this.isDragging() ? 'grabbing' : this.isZoomedIn() ? 'grab' : 'auto';
    const transform = `translate(${this.screenOffsetX()}px, ${this.screenOffsetY()}px) scale(${this.zoomScale()})`;
    const transition = !this.isDragging() ? 'transform 0.3s ease' : '';
    return { transform, cursor, transition };
  });

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.viewSize = StageSizeType.CLOSED;

    if (this.isFullScreenMobile()) {
      this.handleFullscreenChange();
      this.isFullScreenMobile.set(false);
    }
    document.removeEventListener('fullscreenchange', this.onFullScreenChange.bind(this));

    clearTimeout(this.userIsInteractingTimeout);

    // Handle cleanup of tracks without async/await
    void cleanTracks(this.screenStream(), this);

    if (window && this.movementListener) {
      window.removeEventListener('click', this.movementListener, false);
      window.removeEventListener('mousemove', this.movementListener, false);
    }
  }
}
